import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Users, Briefcase, Calendar, BookOpen, MessageCircle, Bookmark, Settings, Home } from "lucide-react";
import { useAuth } from "@/App";
import { useMatches } from "@/components/MatchContext";

/**
 * Persistent dashboard header rendered on every logged-in section page.
 *
 * Tiles always render in this fixed order and color (per user spec):
 *   Member Directory  → Hot pink   (#FF2D87)
 *   Businesses        → Cobalt blue (#5170FF)
 *   Events            → Baby pink  (#FFC2E8) — icon black
 *   Resources         → Black      (#0A0A0A)
 *   Community         → Hot pink   (#FF2D87)
 *   My Portal         → Cobalt blue (#5170FF)
 *
 * The "Hub" landing is /home (separate from /community, which is the
 * discussion forum). A small home icon next to Edit profile links there.
 */
const TILES = [
  { href: "/members",    label: "Member Directory", icon: Users,         color: "#FF2D87" },
  { href: "/businesses", label: "Businesses",       icon: Briefcase,     color: "#5170FF" },
  { href: "/events",     label: "Events",           icon: Calendar,      color: "#FFC2E8", iconColor: "#0A0A0A" },
  { href: "/blog",       label: "Resources",        icon: BookOpen,      color: "#0A0A0A" },
  { href: "/community",  label: "Community",        icon: MessageCircle, color: "#FF2D87" },
  { href: "/dashboard",  label: "My Portal",        icon: Bookmark,      color: "#5170FF" },
];

// Rotating greetings — picked once per page-load, time-of-day aware.
const GENERIC_GREETINGS = [
  "Welcome back, {name}",
  "Good to see you, {name}",
  "Hi {name} — what's on today?",
  "You're in, {name}",
  "Hey {name}, where do you want to start?",
  "Glad you're here, {name}",
  "Let's make something today, {name}",
  "{name}, the ensemble missed you",
  "Ready when you are, {name}",
  "Let's get into it, {name}",
];
const MORNING_GREETINGS = [
  "Good morning, {name}",
  "Morning {name} — coffee in hand?",
  "Rise and shine, {name}",
];
const AFTERNOON_GREETINGS = [
  "Good afternoon, {name}",
  "Afternoon {name} — how's the day going?",
];
const EVENING_GREETINGS = [
  "Good evening, {name}",
  "Evening {name} — wind-down or one more thing?",
];

function pickGreeting(name) {
  if (!name) return "Welcome back";
  const hour = new Date().getHours();
  let pool = [...GENERIC_GREETINGS];
  if (hour >= 5 && hour < 12) pool = [...MORNING_GREETINGS, ...pool];
  else if (hour >= 12 && hour < 17) pool = [...AFTERNOON_GREETINGS, ...pool];
  else pool = [...EVENING_GREETINGS, ...pool];
  const line = pool[Math.floor(Math.random() * pool.length)];
  return line.replace("{name}", name);
}

const DashboardHeader = () => {
  const { user } = useAuth();
  const { incomingCount } = useMatches();
  const location = useLocation();

  const firstName = user?.name?.split(" ")[0] || "";
  const greetingFull = useMemo(() => pickGreeting(firstName), [firstName]);

  if (!user) return null;

  const isActive = (tile) => {
    if (tile.href === "/dashboard")  return location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/");
    if (tile.href === "/members")    return location.pathname === "/members" || location.pathname.startsWith("/profile");
    if (tile.href === "/businesses") return location.pathname === "/businesses" || location.pathname.startsWith("/business/");
    if (tile.href === "/blog")       return location.pathname === "/blog" || location.pathname.startsWith("/blog/");
    if (tile.href === "/community")  return location.pathname === "/community" || location.pathname.startsWith("/community/");
    return location.pathname === tile.href;
  };

  // Split greeting so we can color the name
  const idx = firstName ? greetingFull.indexOf(firstName) : -1;
  const greetingPrefix = idx >= 0 ? greetingFull.slice(0, idx) : greetingFull;
  const greetingSuffix = idx >= 0 ? greetingFull.slice(idx + firstName.length) : "";

  return (
    <header className="bg-white" data-testid="dashboard-header">
      {/* Black hero — overline + heading */}
      <section className="bg-[#0A0A0A] py-10 md:py-14">
        <div className="container-main">
          <p
            className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-[#FF2D87] underline decoration-2 underline-offset-[6px] decoration-[#FF2D87] mb-4 inline-block"
            data-testid="dashboard-overline"
          >
            Community
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-[1.05]">
            Welcome to <span className="text-[#FF2D87] whitespace-nowrap">The Creative Ensemble</span><br className="hidden md:block" /> Community
          </h1>
        </div>
      </section>

      {/* Tiles menu — fixed order, fixed colors, 6 tiles */}
      <nav className="bg-white pt-8 md:pt-10 pb-5" aria-label="Dashboard sections">
        <div className="container-main">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {TILES.map((t) => {
              const active = isActive(t);
              const showBadge = t.href === "/dashboard" && incomingCount > 0;
              return (
                <Link
                  key={t.href}
                  to={t.href}
                  data-testid={`dashboard-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group relative bg-white border-2 border-[#0A0A0A] p-4 md:p-5 transition-all flex flex-col items-start gap-3 ${
                    active
                      ? "shadow-[6px_6px_0_#FF2D87] -translate-y-0.5"
                      : "shadow-[4px_4px_0_#0A0A0A] hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A]"
                  }`}
                >
                  <div
                    className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border-2 border-[#0A0A0A]"
                    style={{ backgroundColor: t.color }}
                  >
                    <t.icon className="w-5 h-5 md:w-6 md:h-6" style={{ color: t.iconColor || "white" }} />
                  </div>
                  <span className={`text-xs md:text-sm font-bold uppercase tracking-widest leading-tight ${active ? "text-[#FF2D87]" : "text-[#0A0A0A]"}`}>
                    {t.label}
                  </span>
                  {showBadge && (
                    <span
                      className="absolute top-2 right-2 bg-[#5170FF] text-white rounded-full text-[10px] px-1.5 min-w-[20px] h-[20px] flex items-center justify-center font-bold border-2 border-white"
                      data-testid={`dashboard-tile-badge-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {incomingCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Rotating greeting + home + edit profile beneath the tiles */}
      <section className="pt-4 pb-6 border-b border-[#E5E5E5]">
        <div className="container-main">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-[#0A0A0A]" data-testid="dashboard-greeting">
              {greetingPrefix}
              <span className="text-[#5170FF]">{firstName}</span>
              {greetingSuffix}
            </h2>
            <div className="flex items-center gap-2">
              <Link
                to="/home"
                title="Back to Hub home"
                className="inline-flex items-center justify-center w-10 h-10 bg-white text-[#0A0A0A] hover:bg-[#FF2D87] hover:text-white border-2 border-[#0A0A0A] rounded-full transition-colors relative"
                data-testid="dashboard-home"
              >
                <Home className="w-4 h-4" />
                {incomingCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#5170FF] text-white rounded-full text-[10px] min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold border-2 border-white" data-testid="dashboard-home-badge">
                    {incomingCount}
                  </span>
                )}
              </Link>
              <Link
                to="/profile"
                className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] font-bold rounded-full px-5 py-2.5 hover:bg-[#FF2D87] hover:text-white hover:border-[#FF2D87] border-2 border-[#0A0A0A] transition-colors text-xs uppercase tracking-widest"
                data-testid="dashboard-edit-profile"
              >
                <Settings className="w-4 h-4" /> Edit profile
              </Link>
            </div>
          </div>
        </div>
      </section>
    </header>
  );
};

export default DashboardHeader;
